import { PhotoFilter } from '@sosocio/creator-types/app';
import _ from 'underscore';
import { reactive } from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';

const samplingRateSettingKeys: ReadonlySet<string> = new Set([
	'analytics.amplitude.samplingRate',
	'analytics.moengage.samplingRate',
]);
const diagnosedInvalidSamplingRateKeys = new Set<string>();

/**
 * Reports whether a numeric setting is a probability constrained to zero through one.
 *
 * @param key - Runtime configuration key being parsed.
 * @returns Whether the setting is a provider sampling rate.
 */
function isSamplingRateSetting(key: string): boolean {
	return samplingRateSettingKeys.has(key);
}

/**
 * Reports a malformed provider sampling rate once per runtime and setting key.
 * Raw values are intentionally excluded because runtime configuration may
 * contain data that does not belong in diagnostics.
 *
 * @param key - Invalid sampling-rate setting key.
 */
function diagnoseInvalidSamplingRate(key: string): void {
	if (
		!isSamplingRateSetting(key)
		|| diagnosedInvalidSamplingRateKeys.has(key)
		|| typeof window === 'undefined'
		|| typeof window.glBugsnagClient === 'undefined'
	) {
		return;
	}

	diagnosedInvalidSamplingRateKeys.add(key);
	window.glBugsnagClient.notify(
		new Error('Invalid analytics sampling rate configuration'),
		(event) => {
			event.severity = 'warning';
			event.addMetadata(
				'runtimeConfig',
				{
					key,
					reason: 'invalid-sampling-rate',
				},
			);
		},
	);
}

/**
 * Retains a numeric setting's typed fallback and diagnoses invalid sampling rates.
 *
 * @param key - Runtime configuration key being parsed.
 * @param fallback - Existing typed value retained when validation fails.
 * @returns The unchanged typed fallback.
 */
function invalidNumberSettingFallback(
	key: string,
	fallback: number,
): number {
	diagnoseInvalidSamplingRate(key);
	return fallback;
}

/**
 * Parses a complete finite numeric setting without truncating decimal values.
 *
 * @param key - Runtime configuration key being parsed.
 * @param value - Raw value supplied by the settings endpoint.
 * @param fallback - Existing typed value retained when the input is invalid.
 * @returns The parsed number, or the typed fallback when validation fails.
 */
function parseNumberSetting(
	key: string,
	value: unknown,
	fallback: number,
): number {
	if (typeof value !== 'number' && typeof value !== 'string') {
		return invalidNumberSettingFallback(
			key,
			fallback,
		);
	}

	const normalizedValue = typeof value === 'string'
		? value.trim()
		: value;

	if (normalizedValue === '') {
		return invalidNumberSettingFallback(
			key,
			fallback,
		);
	}

	const parsedValue = Number(normalizedValue);

	if (!Number.isFinite(parsedValue)) {
		return invalidNumberSettingFallback(
			key,
			fallback,
		);
	}

	if (
		isSamplingRateSetting(key)
		&& (
			parsedValue < 0
			|| parsedValue > 1
		)
	) {
		return invalidNumberSettingFallback(
			key,
			fallback,
		);
	}

	return parsedValue;
}

/**
 * Coerces one runtime setting according to the type of its declared default.
 *
 * @param key - Runtime configuration key being parsed.
 * @param value - Raw value supplied by the settings endpoint.
 * @param currentValue - Current typed value used to select parsing and as fallback.
 * @returns A value compatible with the declared runtime setting type.
 */
function parseSetting(
	key: string,
	value: unknown,
	currentValue: unknown,
): unknown {
	let parsedValue: unknown;

	if (typeof currentValue === 'boolean') {
		parsedValue = Boolean(Number(value));
	} else if (typeof currentValue === 'number') {
		parsedValue = parseNumberSetting(
			key,
			value,
			currentValue,
		);
	} else if (
		typeof value === 'string'
		&& value
		&& value[0] == '['
	) {
		if (value[1] == '\'') {
			const arrayString = value.substring(
				2,
				value.length - 2,
			);
			parsedValue = arrayString.split('\',\'');
		} else {
			const arrayInt = value.substring(
				1,
				value.length - 1,
			);
			const integerValues: number[] = [];
			_.each(
				arrayInt.split(','),
				(int: string) => {
					integerValues.push(parseInt(
						int,
						10,
					));
				},
			);
			parsedValue = integerValues;
		}
	} else {
		parsedValue = value;
	}

	return parsedValue;
}

type AppModule = 'account' | 'cart' | 'store';

@Module({
	namespaced: true,
	name: 'config',
})
export default class Config extends VuexModule {
	'adyen.sdkVersion' = '1.9.7';

	'analytics.amplitude.debug' = false;

	'analytics.amplitude.enabled' = false;

	'analytics.amplitude.externalUserId' = false;

	'analytics.amplitude.id': string | null = null;

	// Should be numbber between 0 and 1, where 1 means 100% of users are sampled
	'analytics.amplitude.samplingRate' = 1;

	'analytics.gtm.debug' = false;

	/*
	 * Host that serves gtm.js. Partners can point this to a first-party
	 * proxy/CDN (e.g. Addingwell) to reduce the impact of tracking protection.
	 */
	'analytics.gtm.domain' = 'www.googletagmanager.com';

	'analytics.gtm.ecommerce' = false;

	'analytics.gtm.email' = false;

	'analytics.gtm.enabled' = false;

	'analytics.gtm.events' = false;

	'analytics.gtm.externalUserId' = false;

	'analytics.gtm.id': string | null = null;

	/*
	 * This is a special property for the Hema label as we need to use a different GTM id for Belgium
	 * TODO We need to create a more universal way of setting properties for specific countries
	 */
	'analytics.gtm.id-be': string | null = null;

	'analytics.moengage.enabled' = false;

	'analytics.moengage.id': string | null = null;

	'analytics.moengage.cluster' = 'DC_2';

	'analytics.moengage.debug' = false;

	'analytics.moengage.externalUserId' = false;

	// Should be numbber between 0 and 1, where 1 means 100% of users are sampled
	'analytics.moengage.samplingRate' = 1;

	'analytics.nativeApp.enabled' = true;

	'analytics.nativeApp.externalUserId' = false;

	'analytics.segment.debug' = false;

	'analytics.segment.enabled' = false;

	'analytics.segment.externalUserId' = false;

	'analytics.segment.id': string | null = null;

	'app.name': string | null = null;

	'app.modules' = reactive<AppModule[]>([]);

	'appSync.url': string | null = null;

	'appDataVersion': string | null = null;

	'auth.guest' = true;

	'auth.internal' = true;

	'auth.loginIcon' = 'far fa-sign-in fa-2x';

	'auth.showStart' = true;

	'auth.showEnd' = false;

	'Bugsnag': string | null = null;

	'createModule.autoForwardThemes' = true;

	'createModule.historyBack' = false;

	'cropModule.threshold' = 0.98;

	'debug.remotejs': string | null = null;

	'editor.frameColors.locked.fill' = '#E4286B';

	'editor.frameColors.locked.stroke' = '#E4286B';

	'editor.frameColors.locked.line' = '#FFFFFF';

	'editor.frameColors.unlocked.fill' = '#00CCCC';

	'editor.frameColors.unlocked.stroke' = '#00CCCC';

	'editor.frameColors.unlocked.line' = '#FFFFFF';

	'editor.vectorEditColors.nonHoveredOpacity' = 0.1;

	'features.askNativeAppReview' = true;

	'features.crossSell' = true;

	'features.countryDetection' = true;

	'features.editor.zoom' = true;

	'features.imageEffects' = reactive<PhotoFilter[]>([
		'boost',
		'grey',
		'sepia',
		'negate',
	]);

	'features.imageMasks' = true;

	'features.offerProjectLink' = false;

	'features.pms' = false;

	'features.qrUpload' = true;

	'features.smartystreets': string | null = null;

	'features.smartZoom' = false;

	'features.thinLineDetection' = true;

	'features.upsell' = false;

	'features.vectorization' = false;

	'features.vectorizationPhotoDetection' = true;

	'features.webPush' = false;

	'sticker.resizeMaxSize': number | null = null;

	'sticker.resizeMinSize': number | null = null;

	'sticker.maxSizeByPage': number = 0.25;

	'flagsmith.enabled' = false;

	'flagsmith.id': string | null = null;

	'header.account' = true;

	'header.menu.categories' = true;

	'header.menu.appReview' = false;

	'header.menu.show' = true;

	'header.show.native' = true;

	'header.show.web' = true;

	'header.status' = true;

	// Temporary flag to enable the HelloPrint legacy integration for photo products
	'helloprint.legacy' = false;

	'kiosk.idleTimeoutSeconds' = 5 * 60;

	'links.legal': string | null = null;

	'links.extraCheckmark': string | null = null;

	'links.referral': string | null = null;

	'links.blog': string | null = null;

	'logoColors.convertToNColorsThreshold' = 50;

	'logoColors.mergeSimilarThreshold' = 2;

	'LogRocket.id': string | null = null;

	'LogRocket.enabled' = true;

	'LogRocket.externalUserId' = false;

	'LogRocket.filter.url': string | null = null;

	'metricUnits': 'mm' | 'in' = 'mm';

	'pageMargin.warningThreshold' = 0.95;

	'pageSize.bestFit.precision' = 0.97;

	'partnerID' = 0;

	'photoPreviewSize' = 1500;

	'photoPreviewSizeLocal' = 1500;

	'photoScalingBaseUrl': string | null = null;

	'projectImageBaseUrl': string | null = null;

	'Raygun': string | null = null;

	'share': string[] = [];

	'sse': string | null = null;

	'support.chat': 'zopim' | 'salesforce' = 'zopim';

	'support.email': string | null = null;

	'support.zopim.apikey': string | null = null;

	'support.zopim.tag': string | null = null;

	'textEncodingSupport' = reactive<string[]>(['latin']);

	'textColors.convertToInverseColorThreshold' = 50;

	'tours.cardgame_overview.show' = false;

	'tours.editor_module.show' = false;

	'tours.photoframebox_overview.show' = false;

	'upload.fileSizeLimit' = '20MB'; // Maximum filesize the user can upload

	'upload.accelerate' = false;

	'upload.eps' = false;

	'upload.heic' = true;

	'upload.pdf' = false;

	'upload.svg' = false;

	'upscaling.maxOutput' = 128 * 1000000; // 128 megapixels

	'upscaling.recommendedMaxScale' = 8;

	'userAccount.requiredFields' = reactive<string[]>([]);

	'userAccount.route' = 'user';

	'vectorization.maxColors' = 20; // Maximum number of colors in a vectorized image

	'webfont.timeout' = 100; // Delay in setting webfont loaded (fix for iOS)

	// For integration with external webshops, this is path we POST to that will perform the redirect to the webshop
	'webshop.external.endpoint': string | null = '/api/webshop/redirect';

	'webshop.external.method': 'GET' | 'POST' = 'GET';

	[key: string]: any;

	get hasAccountModule() {
		return this['app.modules'].indexOf('account') >= 0;
	}

	get hasCartModule() {
		return this['app.modules'].indexOf('cart') >= 0;
	}

	get hasStoreModule() {
		return this['app.modules'].indexOf('store') >= 0;
	}

	get labelTagName() {
		return this['app.name'];
	}

	/**
	 * Applies one already-parsed runtime setting to the module state.
	 *
	 * @param o - Setting key and type-compatible value.
	 */
	@Mutation
	private _applySetting(o: { key: string;
		value: unknown }) {
		this[o.key] = o.value;
	}

	@Action({
		rawError: true,
	})
	public addModels(
		data: {
			[key: string]: any;
		} = {},
	) {
		const { commit } = this.context;

		// Add settings
		const pairs = _.pairs(data);
		pairs.forEach(([
			key,
			value,
		]) => {
			const setting = parseSetting(
				key,
				value,
				this[key],
			);
			commit(
				'_applySetting',
				{
					key,
					value: setting,
				},
			);
		});
	}
}
